import { BankStatementImport } from "@/components/modals/BankStatementImport";
import { Hint } from "@/components/portfolio/Hint";
import { MyEstateSorter } from "@/components/portfolio/MyEstateSorter";
import { hasUnverifiedTransactionPeriods } from "@/components/portfolio/transactions/transactionVerification";
import { RoundButtonWithIcon } from "@/components/tools/RoundButtonWithIcon";
import { PanelHeader } from "@/components/ui/PanelHeader";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import type { ImportedBankTransaction, TransactionPreset } from "@/types";
import { PencilIcon } from "@heroicons/react/24/solid";

export function PortfolioEstateAnalyticsPanel() {
  const { language } = useLanguage();
  const copy = {
    cs: {
      title: "Analytika nemovitosti",
      description: "Pohled na finance nemovitosti.",
      editTransactions: "Upravit transakce",
      analyticsHint: "Podívejte se na vývoj cen",
      importHint:
        "Importujte bankovní výpis pro automatické ověření transakcí.",
      importHistoryHint:
        "Importujte bankovní výpis a transakce se načtou automaticky. V dalším kroku vyberete, které chcete uložit.",
    },
    en: {
      title: "Property analytics",
      description: "Income, expenses, and monthly trends.",
      editTransactions: "Edit transactions",
      analyticsHint: "View price development",
      importHint:
        "Import a bank statement to verify transactions automatically.",
      importHistoryHint:
        "Import a bank statement to load transactions automatically. In the next step, choose which ones to save.",
    },
  }[language];
  const { selectedEstate, openEditModalTransactions, isGraphView } =
    useEstateContext();
  const now = new Date();
  const currentPeriod = `${String(now.getMonth() + 1).padStart(2, "0")} ${now.getFullYear()}`;
  const hasUnverifiedTransactions = selectedEstate
    ? hasUnverifiedTransactionPeriods(
        selectedEstate.transactions ?? [],
        currentPeriod,
      )
    : false;

  const handleAddImportedTransactions = (
    importedTransactions: ImportedBankTransaction[],
  ) => {
    const toIsoDate = (date: string) => {
      const [year, month, day] = date.split("-").map(Number);
      return new Date(year, month - 1, day, 12).toISOString();
    };
    const transactionsBySeries = new Map<string, ImportedBankTransaction[]>();
    importedTransactions.forEach((transaction) => {
      const seriesTransactions =
        transactionsBySeries.get(transaction.seriesId) ?? [];
      seriesTransactions.push(transaction);
      transactionsBySeries.set(transaction.seriesId, seriesTransactions);
    });

    const groupedTransactions: Array<{
      transactions: ImportedBankTransaction[];
      seriesSegmentIndex: number;
    }> = [];
    transactionsBySeries.forEach((seriesTransactions) => {
      const sortedTransactions = [...seriesTransactions].sort((first, second) =>
        first.date.localeCompare(second.date),
      );
      let currentGroup: ImportedBankTransaction[] = [];
      let segmentIndex = 0;

      sortedTransactions.forEach((transaction) => {
        const previous = currentGroup.at(-1);
        const belongsToCurrentSegment =
          previous &&
          previous.amount === transaction.amount &&
          previous.transactionType === transaction.transactionType &&
          previous.category === transaction.category;
        if (!belongsToCurrentSegment && currentGroup.length > 0) {
          groupedTransactions.push({
            transactions: currentGroup,
            seriesSegmentIndex: segmentIndex++,
          });
          currentGroup = [];
        }
        currentGroup.push(transaction);
      });

      if (currentGroup.length > 0) {
        groupedTransactions.push({
          transactions: currentGroup,
          seriesSegmentIndex: segmentIndex,
        });
      }
    });

    const presets: TransactionPreset[] = groupedTransactions
      .sort((first, second) =>
        first.transactions[0].date.localeCompare(second.transactions[0].date),
      )
      .map(({ transactions, seriesSegmentIndex }) => {
        const transaction = transactions[0];
        const transactionDate = toIsoDate(transaction.date);
        const recurrence = ["PROPERTY_TAX", "INSURANCE"].includes(
          transaction.category,
        )
          ? "YEARLY"
          : "MONTHLY";
        return {
          id: null,
          originalId: 0,
          name: transaction.suggestedName,
          amount: transaction.amount,
          transactionType: transaction.transactionType,
          recurrence,
          dateFrom: transactionDate,
          originalDateFrom: transactionDate,
          dateTo: null,
          isPrediction: false,
          taxDeductible: false,
          showInDashboard: true,
          bankImportId: transaction.importId,
          bankImportSeriesId: transaction.seriesId,
          bankImportFileName: transaction.bankImportFileName,
          bankImportFormat: transaction.bankImportFormat,
          bankImportTransactionCount: transaction.bankImportTransactionCount,
          bankImportPeriods: transaction.bankImportPeriods,
          replaceRecurringTransactionId:
            seriesSegmentIndex === 0
              ? (transactions.find(
                  (item) => item.replaceRecurringTransactionId != null,
                )?.replaceRecurringTransactionId ?? null)
              : null,
          verifiedPeriods: Array.from(
            new Set(
              transactions.map((item) => {
                const [year, month] = item.date.split("-");
                return `${month} ${year}`;
              }),
            ),
          ),
        };
      });

    openEditModalTransactions(presets);
  };

  return (
    <PanelHeader
      title={copy.title}
      description={copy.description}
      actions={
        <MyEstateSorter showPredictionToggle={isGraphView}>
          {selectedEstate && (
            <div className="relative">
              <BankStatementImport
                estateId={selectedEstate.id}
                onAdd={handleAddImportedTransactions}
              />
              <div className="absolute -right-8 top-12 z-30 flex flex-col items-end gap-3 lg:flex-row lg:items-start">
                <Hint
                  preference="analyticsViewTip"
                  text={copy.analyticsHint}
                  showArrow={false}
                />
                {hasUnverifiedTransactions ? (
                  <Hint
                    preference="transactionImportTip"
                    text={copy.importHint}
                  />
                ) : (
                  <Hint
                    preference="transactionHistoryImportTip"
                    text={copy.importHistoryHint}
                  />
                )}
              </div>
            </div>
          )}
          <RoundButtonWithIcon
            icon={PencilIcon}
            text={copy.editTransactions}
            onClick={() => openEditModalTransactions()}
          />
        </MyEstateSorter>
      }
    />
  );
}
